/**
 * Three routes, hand-rolled.
 *
 *   /                      start a session
 *   /s/:id?t=<token>       viewer
 *   /s/:id/remote?c=<code> controller — exchanges the QR code for a token
 *
 * A router dependency would earn its weight at ten routes; at three it would
 * only add a build artefact.
 */
import { useEffect, useState } from 'react';
import { ApiError, joinAsController } from './lib/api.js';
import { SessionPage } from './routes/SessionPage.js';
import { StartPage } from './routes/StartPage.js';

type Route =
  | { kind: 'start' }
  | { kind: 'viewer'; sessionId: string; token: string }
  | { kind: 'controller'; sessionId: string; code: string }
  | { kind: 'not-found'; reason: string };

function parseRoute(location: Location): Route {
  const params = new URLSearchParams(location.search);
  const match = /^\/s\/([A-Za-z0-9_-]{1,128})(\/remote)?\/?$/.exec(location.pathname);
  if (!match) return location.pathname === '/' ? { kind: 'start' } : { kind: 'not-found', reason: 'Unknown page.' };

  const sessionId = match[1]!;
  if (match[2]) {
    const code = params.get('c');
    if (!code) return { kind: 'not-found', reason: 'This phone link is missing its pairing code.' };
    return { kind: 'controller', sessionId, code };
  }

  const token = params.get('t');
  if (!token) return { kind: 'not-found', reason: 'This link is missing its session token.' };
  return { kind: 'viewer', sessionId, token };
}

export function App(): JSX.Element {
  const [route] = useState(() => parseRoute(window.location));

  if (route.kind === 'start') return <StartPage />;
  if (route.kind === 'viewer') {
    return <SessionPage sessionId={route.sessionId} token={route.token} role="viewer" />;
  }
  if (route.kind === 'controller') {
    return <ControllerGate sessionId={route.sessionId} code={route.code} />;
  }
  return (
    <div className="start">
      <h1>Nothing here</h1>
      <p className="error">{route.reason}</p>
      <a className="primary button" href="/">
        Start a session
      </a>
    </div>
  );
}

/**
 * Trades the QR's short-lived code for a controller token before the session
 * page opens a socket. Keeping it a separate step means a stale QR fails with a
 * readable message rather than a rejected handshake.
 */
function ControllerGate({ sessionId, code }: { sessionId: string; code: string }): JSX.Element {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void joinAsController(sessionId, code)
      .then((result) => {
        if (!cancelled) setToken(result.controllerToken);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 410 || err.reason === 'expired') {
            setError('That pairing code has expired. Ask for a new code on the start screen.');
            return;
          }
          if (err.reason === 'locked') {
            setError('Pairing was locked after too many wrong codes. Start a new session.');
            return;
          }
          setError(err.message);
          return;
        }
        setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, code]);

  if (error) {
    return (
      <div className="start">
        <h1>Could not pair</h1>
        <p className="error">{error}</p>
      </div>
    );
  }
  if (!token) {
    return (
      <div className="start">
        <h1>Pairing…</h1>
      </div>
    );
  }
  return <SessionPage sessionId={sessionId} token={token} role="controller" />;
}
